package gr.tech.pdfgenerator.sections

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.Resize
import com.varabyte.kobweb.compose.css.disabled
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import gr.tech.pdfgenerator.util.formatCurrency
import gr.tech.pdfgenerator.util.parseCurrencyInput
import gr.tech.pdfgenerator.styles.InputLabelStyle
import gr.tech.pdfgenerator.styles.stringInputStyle
import gr.tech.pdfgenerator.util.CONSTANTS.FONT_FAMILY
import kotlinx.browser.document
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLTextAreaElement


/*
    ----------------------------------
        UPDATES !~ONLY~! AFTER SUBMIT
    ----------------------------------
 */
@Composable
fun SubmitStringInputLabel(
    label: String,
    id: String,
    placeholder: String = "",
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .margin(bottom = 6.px)
    ) {
        Label(
            forId = id,
            attrs = InputLabelStyle.toAttrs()
        ) {
            Text(label)
        }

        Input(
            type = InputType.Text,
            attrs = stringInputStyle.toModifier()
                .id(id)
                .toAttrs()
        )
    }
}

@Composable
fun SubmitTextAreaInputLabel(
    label: String,
    id: String,
    placeholder: String = "",
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .margin(bottom = 6.px)
    ) {
        Label(
            forId = id,
            attrs = InputLabelStyle.toAttrs()
        ) {
            Text(label)
        }

        TextArea(
            attrs = stringInputStyle.toModifier()
                .resize(Resize.None)
                .id(id)
                .toAttrs()
        )
    }
}

@Composable
fun SubmitDateInputLabel(
    label: String,
    id: String
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .margin(bottom = 6.px)
    ) {
        Label(
            forId = id,
            attrs = InputLabelStyle.toAttrs()
        ) {
            Text(label)
        }

        Input(
            type = InputType.Date,
            attrs = stringInputStyle.toModifier()
                .id(id)
                .toAttrs()
        )
    }
}


/*
    ----------------------------------
        UPDATED AFTER TYPING INPUTS
    ----------------------------------
 */

//Small helper functions
fun sanitizeNumber(input: String): Long? {
    val sanitizedInput = input.replace("[^0-9.]".toRegex(), "")
    return sanitizedInput.toLongOrNull()
}

//Small helper functions
fun sanitizeDecimal(input: String): Double? {
    val sanitizedInput = input.replace("[^0-9.]".toRegex(), "")
    return sanitizedInput.toDoubleOrNull()
}

fun formatPercentage(value: Double): String {
    return "${value.times(100)}%"
}

@Composable
fun StringInputLabel(
    label: String,
    id: String,
    required: Boolean = false, //If true, shows the valid color red text.
    placeholder: String = "",
    initialValue: String? = null,
    onInput: (String) -> Unit,
) {
    //Validation
    var valid by remember { mutableStateOf(!initialValue.isNullOrBlank()) } //Checks to see if string is valid entry.
    var focused by remember { mutableStateOf(false) } //Checks to see if user entered input. Used to stop red label immediately.

    var inputValue by remember { mutableStateOf(initialValue ?: "") }
    var hasChanged by remember { mutableStateOf(false) }

    //UI
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .margin(bottom = 6.px)
    ) {
        Label(
            forId = id,
            attrs = InputLabelStyle.toModifier()
                .color(if (!valid && focused && required) Colors.Red else Colors.Black)
                .toAttrs()
        ) {
            Text(label)
        }

        Input(
            type = InputType.Text,
            attrs = stringInputStyle.toModifier()
                .toAttrs {
                    id(id)
                    name(label)
                    value(inputValue)
                    placeholder(placeholder)
                    autoComplete(AutoComplete.off)
                    onInput {
                        inputValue = it.value
                        hasChanged = true
                    }
                    onBlur {
                        if (hasChanged) {
                            if (inputValue.isNotEmpty() && inputValue.isNotBlank()) {
                                valid = true
                                hasChanged = false
                            } else {
                                if (required) {
                                    valid = false
                                }
                                hasChanged = false
                            }
                            onInput(inputValue)
                        }
                        focused = true
                    }
                }
        )
    }
}

@Composable
fun TextAreaInputLabel(
    label: String,
    id: String,
    required: Boolean = false,
    placeholder: String = "",
    initialValue: String? = null,
    onInput: (String) -> Unit,
) {
    var valid by remember { mutableStateOf(!initialValue.isNullOrBlank()) }
    var focused by remember { mutableStateOf(false) }
    var inputValue by remember { mutableStateOf(initialValue ?: "") }
    var hasChanged by remember { mutableStateOf(false) }

    LaunchedEffect(Unit) {
        val area = (document.getElementById(id)) as HTMLTextAreaElement
        area.value = inputValue
    }

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .fillMaxHeight()
            .margin(bottom = 6.px)
    ) {
        Label(
            forId = id,
            attrs = InputLabelStyle.toModifier()
                .color(if (!valid && focused && required) Colors.Red else Colors.Black)
                .toAttrs()
        ) {
            Text(label)
        }

        TextArea(
            attrs = stringInputStyle.toModifier()
                .minHeight(100.px)
                .height(150.px)
                .maxHeight(300.px)
                .resize(Resize.Vertical)
                .toAttrs {
                    id(id)
                    name(label)
                    placeholder(placeholder)
                    autoComplete(AutoComplete.off)
                    onInput {
                        inputValue = it.value
                        hasChanged = true
                    }
                    onBlur {
                        if (hasChanged) {
                            if (inputValue.isNotBlank()) {
                                valid = true
                            } else if (required) {
                                valid = false
                            }
                            hasChanged = false
                            onInput(inputValue)
                        }
                        focused = true
                    }
                }
        )
    }
}


@Composable
fun DateInputLabel(
    label: String,
    id: String,
    placeholder: String = "",
    initialValue: String? = null,
    onInput: (String) -> Unit,
) {
    //Validation
    var valid by remember { mutableStateOf(false) } //Checks to see if string is valid entry.
    var focused by remember { mutableStateOf(false) } //Checks to see if user entered input. Used to stop red label immediately.

    var inputValue by remember { mutableStateOf(initialValue ?: "") }
    var hasChanged by remember { mutableStateOf(false) }

    //UI
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .margin(bottom = 6.px)
    ) {
        Label(
            forId = id,
            attrs = InputLabelStyle.toModifier()
                .color(if (!valid && focused) Colors.Red else Colors.Black)
                .toAttrs()
        ) {
            Text(label)
        }

        Input(
            type = InputType.Date,
            attrs = stringInputStyle.toModifier()
                .toAttrs {
                    id(id)
                    name(label)
                    value(inputValue)
                    placeholder(placeholder)
                    autoComplete(AutoComplete.off)
                    onInput {
                        inputValue = it.value
                        hasChanged = true
                    }
                    onBlur {
                        if (hasChanged) {
                            if (inputValue.isNotEmpty() && inputValue.isNotBlank()) {
                                valid = true
                                hasChanged = true
                            } else {
                                valid = false
                                hasChanged = false
                            }
                            onInput(inputValue)
                        }
                        focused = true
                    }
                }
        )
    }
}

@Composable
fun LongInputLabel(
    label: String,
    id: String,
    placeholder: Long = 0,
    initialValue: String? = null,
    onAmountChanged: (Long) -> Unit,
) {
    var valid by remember { mutableStateOf(false) }
    var focused by remember { mutableStateOf(false) } //Checks to see if user entered input. Used to stop red label immediately.

    var inputValue by remember { mutableStateOf(initialValue ?: "") }
    var hasChanged by remember { mutableStateOf(false) }

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .margin(bottom = 6.px)
    ) {
        Label(
            forId = id,
            attrs = InputLabelStyle.toModifier()
            .color(if (!valid && focused) Colors.Red else Colors.Black)
            .toAttrs()
        ) {
                Text(label)
        }

        Input(
            type = InputType.Text,
            attrs = stringInputStyle.toModifier()
                .toAttrs {
                    value(if (inputValue != "0") inputValue else "")
                    placeholder(placeholder.toString())
                    autoComplete(AutoComplete.off)
                    onInput {
                        inputValue = it.value
                        hasChanged = true
                    }
                    onBlur {
                        if (hasChanged) {
                            if (inputValue.isNotEmpty() && inputValue.isNotBlank()) {
                                valid = true
                                hasChanged = true
                            } else {
                                valid = false
                                hasChanged = false
                                onAmountChanged(0)
                            }

                            val cleanString = sanitizeNumber(inputValue)
                            if (cleanString != null) {
                                valid = true
                                onAmountChanged(cleanString)
                            } else {
                                valid = false
                                hasChanged = false
                            }
                        } else {
                            onAmountChanged(placeholder)
                        }
                        focused = true
                    }
                }
        )
    }
}

@Composable
fun DollarInputLabel(
    label: String,
    id: String,
    placeholder: Long = 0,
    initialValue: String? = null,
    onAmountChanged: (Long) -> Unit
) {
    var valid by remember { mutableStateOf(false) }
    var focused by remember { mutableStateOf(false) } //Checks to see if user entered input. Used to stop red label immediately.

    var inputValue by remember { mutableStateOf(initialValue?.toLong()?.div(100)?.toString() ?: "") }
    var hasChanged by remember { mutableStateOf(false) }

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .margin(bottom = 6.px)
    ) {
        Label(
            forId = id,
            attrs = InputLabelStyle.toModifier()
                .color(if (!valid && focused) Colors.Red else Colors.Black)
                .toAttrs()
        ) {
            Text(label)
        }

        Input(
            type = InputType.Text,
            attrs = stringInputStyle.toModifier()
                .toAttrs {
                    value(if (inputValue != "0") inputValue else "")
                    autoComplete(AutoComplete.off)
                    placeholder(formatCurrency(placeholder))
                    onInput {
                        inputValue = it.value
                        hasChanged = true
                    }
                    onBlur {
                        if (hasChanged) {
                            if (inputValue.isNotEmpty() && inputValue.isNotBlank()) {
                                valid = true
                                hasChanged = true
                            } else {
                                valid = false
                                hasChanged = false
                                onAmountChanged(0)
                            }

                            val amountCents = parseCurrencyInput(inputValue)
                            if (amountCents != null) {
                                valid = true
                                onAmountChanged(amountCents)
                            } else {
                                valid = false
                                hasChanged = false
                            }
                        } else {
                            onAmountChanged(placeholder)
                        }
                        focused = true
                    }
                }
        )
    }
}

@Composable
fun DoubleInputLabel(
    label: String,
    id: String,
    placeholder: Double = 0.0,
    initialValue: Double? = null,
    onAmountChanged: (Double) -> Unit
) {
    var valid by remember { mutableStateOf(true) }
    var focused by remember { mutableStateOf(false) } //Checks to see if user entered input. Used to stop red label immediately.

    var hasChanged by remember { mutableStateOf(false) }

    // Ensure value updates are reflected
    var inputValue by remember { mutableStateOf(initialValue.toString()) }

    // Ensure updates propagate when `initialValue` changes
    LaunchedEffect(initialValue) {
        inputValue = initialValue.toString()
    }

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .margin(bottom = 6.px)
    ) {
        Label(
            forId = id,
            attrs = InputLabelStyle.toModifier()
                .color(if (!valid && focused) Colors.Red else Colors.Black)
                .toAttrs()
        ) {
            Text(label)
        }

        Input(
            type = InputType.Text,
            attrs = stringInputStyle.toModifier()
                .toAttrs {
                    value(inputValue)
                    placeholder(placeholder.toString())
                    onInput {
                        inputValue = it.value
                        hasChanged = true
                    }
                    onBlur {
                        if (hasChanged) {
                            if (inputValue.isNotEmpty() && inputValue.isNotBlank()) {
                                valid = true
                                hasChanged = true
                            } else {
                                valid = false
                                hasChanged = false
                                onAmountChanged(0.0)
                            }

                            val cleanNumber = sanitizeDecimal(inputValue)
                            if (cleanNumber != null) {
                                valid = true
                                onAmountChanged(cleanNumber)
                            } else {
                                valid = false
                                hasChanged = false
                            }
                        } else {
                            onAmountChanged(placeholder)
                        }
                        focused = true
                    }
                }
        )
    }
}

@Composable
fun PercentInputLabel(
    label: String,
    id: String,
    placeholder: Double = 0.0,
    initialValue: Double? = null,
    onAmountChanged: (Double) -> Unit,
) {
    var valid by remember { mutableStateOf(false) }
    var focused by remember { mutableStateOf(false) } //Checks to see if user entered input. Used to stop red label immediately.

    var inputValue by remember { mutableStateOf(initialValue?.times(100)?.toString() ?: "") }
    var hasChanged by remember { mutableStateOf(false) }

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .margin(bottom = 6.px)
    ) {
        Label(
            forId = id,
            attrs = InputLabelStyle.toModifier()
                .color(if (!valid && focused) Colors.Red else Colors.Black)
                .toAttrs()
        ) {
            Text(label)
        }

        Input(
            type = InputType.Text,
            attrs = stringInputStyle.toModifier()
                .toAttrs {
                    value(if (inputValue != "0") inputValue else "")
                    placeholder("${placeholder.times(100).toString()}%")
                    onInput {
                        inputValue = it.value
                        hasChanged = true
                    }
                    onBlur {
                        if (hasChanged) {
                            if (inputValue.isNotEmpty() && inputValue.isNotBlank()) {
                                valid = true
                                hasChanged = true
                            } else {
                                valid = false
                                hasChanged = false
                                onAmountChanged(0.0)
                            }

                            val cleanNumber = inputValue.toIntOrNull()
                            if (cleanNumber != null) {
                                valid = true
                                onAmountChanged(cleanNumber / 100.00)
                            } else {
                                valid = false
                                hasChanged = false
                            }
                        } else {
                            onAmountChanged(placeholder)
                        }
                        focused = true
                    }
                }
        )
    }
}

@Composable
fun ToggleButton(
    label: String,
    id: String,
    disabled: Boolean = false,
    initialValue: Boolean = false,
    onToggle: (Boolean) -> Unit,
) {
    var toggled by remember { mutableStateOf(initialValue) }

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .margin(bottom = 6.px),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Input(
            type = InputType.Checkbox,
            attrs = Modifier.toAttrs {
                id(id)
                disabled(disabled)
                checked(toggled)
                onChange {
                    toggled = !toggled
                    onToggle(toggled)
                }
            }
        )

        Label(
            forId = id,
            attrs = InputLabelStyle.toModifier().toAttrs()
        ) {
            Text(label)
        }
    }
}


@Composable
fun CustomDropdown(
    itemList: List<String>,
    id: String,
    initialValue: String? = null,
    onItemSelected: (String) -> Unit,
) {
    var selectedState by remember { mutableStateOf(initialValue ?: "") }

    Select(
        attrs = Modifier
            .id(id)
            .fillMaxWidth()
            .overflow(Overflow.Scroll)
            .height(50.px)
            .fontSize(18.px)
            .toAttrs()
    ) {
        Option(
            value = "Select State",
            attrs = Modifier
                .fontFamily(FONT_FAMILY)
                .color(Colors.Black)
                .disabled(true)
                .toAttrs {
                    if (selectedState.isEmpty()) {
                        attr("selected", "true")
                    }
                }
        ) {
            Text("Select State")
        }
        itemList.forEachIndexed { index, item ->
            Option(
                value = item,
                attrs = Modifier
                    .fontFamily(FONT_FAMILY)
                    .color(Colors.Black)
                    .fontSize(18.px)
                    .onClick {
                        selectedState = item
                        onItemSelected(selectedState)
                    }
                    .toAttrs{
                        if (selectedState == item) {
                            attr("selected", "true")
                        }
                    }
            ) {
                Text(item)
            }
        }
    }

}